import React, { useEffect, useState } from "react";
import {
    CrossIcon,
    StyledLabel,
    StyledTextInput,
} from "@aureskonnect/react-ui";
import { useTranslation } from "react-i18next";
import { AvForm, AvGroup } from "availity-reactstrap-validation";
import { useSnapshot } from "valtio";
import classnames from "classnames";

import {
    store as appStore,
    setIsActionsClicked,
    setIsEdited,
    setActions,
} from "../../store";
import { store as userStore } from "@components/VerticalLayout/store";
import { setSelectedRow } from "@pages/SettingGeneral/Language/store";

import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";
import {
    CustomCardHeader,
    CustomCardBody,
    CustomCardFooter,
} from "@components/Common/CustomCardStyled";
import { ConfirmationEditMessage } from "@components/Common/ConfirmationEditMessage";
import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { useFetchGlobal } from "../../../../../api/hooks";
import { showSuccessToast } from "@components/Common/SuccessToast";
import ErrorToast from "@components/Common/ErrorTost";
import { fetchPosGeneralSettings } from "../PosConfigurationModal/posConfigService";
import { addProject, editProject } from "../api";

type AddProjectProps = {
    setIsAddProject: (value: boolean) => void;
    setDataIsUpdated: (value: boolean | number) => void;
    isAddProject: boolean;
    isAddNewApplicationButtonClicked: boolean;
    selectedRows: Record<string, any>;
    data: Record<string, any>;
    shopId: string;
};

export function AddProject({
    setIsAddProject,
    isAddProject,
    setDataIsUpdated,
    isAddNewApplicationButtonClicked,
    selectedRows,
    data,
    shopId,
}: AddProjectProps) {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();
    const {
        oneShop,
        userID,
        franchiseID,
        shopID,
        operatorID,
        shopName,
    } = useSnapshot(userStore);
    const { isPlusIconClicked, isEdited, isActionsClicked } = useSnapshot(
        appStore
    );
    const [inputDesignation, setInputDesignation] = useState(false);
    const [isClickedBouttonValider, setIsClickedBouttonValider] = useState(
        true
    );
    const [isModalOpened, setIsModalOpened] = useState(false);
    const [isFetchingTemplate, setIsFetchingTemplate] = useState(false);
    const [designation, setDesignation] = useState("");
    const [remark, setRemark] = useState("");
    const [title, setTitle] = useState("");

    async function handleAddProject(template: Record<string, unknown> = {}) {
        setIsClickedBouttonValider(false);
        try {
            const result = await addProject(fetchGlobal, {
                userID: userID.toString(),
                franchiseID,
                operatorID,
                oneShop,
                shopID,
                shopId,
                shopName,
                designation,
                remark,
                template,
            });
            if (result.error) throw new Error("error");
            showSuccessToast(t("The project is successfully added")!);
            setDesignation("");
            setRemark("");
            setIsClickedBouttonValider(true);
            setDataIsUpdated(true);
            setIsEdited(false);
        } catch {
            ErrorToast(`${t("There's an error")!}`);
        }
    }

    const handleAddWithDefaultTemplate = async () => {
        setIsFetchingTemplate(true);
        try {
            const template = await fetchPosGeneralSettings(franchiseID, shopId);
            await handleAddProject(
                (template as unknown) as Record<string, unknown>
            );
        } catch {
            ErrorToast(`${t("There's an error")!}`);
        } finally {
            setIsFetchingTemplate(false);
        }
    };

    async function handleEditProject() {
        setIsClickedBouttonValider(false);
        try {
            const result = await editProject(fetchGlobal, {
                userID: userID.toString(),
                franchiseID,
                operatorID,
                oneShop,
                shopID,
                shopId,
                designation,
                remark,
                selectedRows,
                t,
            });
            if (result.error) throw new Error("error");
            showSuccessToast(`${t("The project was successfully changed")!}`);
            setDesignation("");
            setRemark("");
            setDataIsUpdated(true);
            setIsClickedBouttonValider(true);
            setIsAddProject(!isAddProject);
            setIsEdited(false);
        } catch {
            ErrorToast(`${t("There's an error")!}`);
        }
    }

    useEffect(() => {
        if (selectedRows.projectId) {
            setTitle(t("Modify project"));
            setDesignation(selectedRows[t("Designation")]);
            setRemark(selectedRows[t("Note")]);
        } else {
            setTitle(t("Add a project"));
            setDesignation("");
            setRemark("");
        }
        setInputDesignation(false);
        setIsClickedBouttonValider(true);
        // eslint-disable-next-line
    }, [selectedRows, t, isAddNewApplicationButtonClicked, isAddProject]);

    useEffect(() => {
        if (isPlusIconClicked) {
            setInputDesignation(false);
        }
    }, [isPlusIconClicked]);

    useEffect(() => {
        if (Object.values(selectedRows).length !== 0) {
            if (selectedRows[t("Designation")] !== "") {
                setInputDesignation(false);
            }
        }
        setIsClickedBouttonValider(true);
        // eslint-disable-next-line
    }, [selectedRows]);

    const handleCloseWithConfirm = () => {
        if (isEdited === true) {
            setIsActionsClicked(!isActionsClicked);
            setActions(() => {
                setIsEdited(false);
                setIsAddProject(!isAddProject);
                setIsClickedBouttonValider(true);
            });
        } else {
            setIsAddProject(!isAddProject);
        }
    };

    const handleValidate = () => {
        if (designation === "") {
            setInputDesignation(true);
            return;
        }

        const isUnchanged =
            !isEdited ||
            (selectedRows[t("Designation")] === designation &&
                selectedRows[t("Note")] === remark);

        if (isUnchanged) {
            setIsModalOpened(true);
        } else if (
            designation.trim() !== "" &&
            selectedRows.projectId === undefined
        ) {
            handleAddWithDefaultTemplate();
        } else if (
            designation.trim() !== "" &&
            selectedRows.projectId !== undefined
        ) {
            handleEditProject();
        }
    };

    const showDesignationError =
        inputDesignation || (designation.trim() === "" && designation !== "");

    return (
        <React.Fragment>
            <AvForm className="card">
                <CustomCardHeader
                    className="d-flex align-items-center justify-content-between"
                    style={{ maxHeight: "76px" }}
                >
                    <h5 className="pt-3">{title}</h5>
                    <CrossIcon
                        style={{ cursor: "pointer" }}
                        height={13}
                        width={13}
                        onClick={handleCloseWithConfirm}
                    />
                </CustomCardHeader>
                <CustomCardBody className="pl-3 pr-5">
                    <AvGroup>
                        <StyledLabel
                            htmlFor="name"
                            className="required__clz mt-3"
                        >
                            {t("Designation")}
                        </StyledLabel>
                        <StyledTextInput
                            autocomplete="off"
                            className={classnames("sco_inp_designation", {
                                input__clz: showDesignationError,
                            })}
                            id="designation"
                            name="designation"
                            placeholder={t("Write")}
                            type="text"
                            onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                                const value = e.target.value;
                                setDesignation(value);
                                setIsEdited(true);
                                setIsClickedBouttonValider(true);
                                setInputDesignation(false);
                            }}
                            value={designation}
                            autoFocus
                        />
                        {showDesignationError && (
                            <div
                                style={{
                                    width: "100%",
                                    marginTop: "0.25rem",
                                    fontSize: "80%",
                                    color: "#f46a6a",
                                }}
                            >
                                {t("Please enter a designation")}
                            </div>
                        )}
                    </AvGroup>
                    <AvGroup>
                        <StyledLabel className="mt-3" htmlFor="remark">
                            {t("Remark")}
                        </StyledLabel>
                        <StyledTextInput
                            autocomplete="off"
                            id="remark"
                            name="remark"
                            placeholder={t("Write")}
                            type="text"
                            onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                                setIsClickedBouttonValider(true);
                                setRemark(e.target.value);
                                setIsEdited(true);
                            }}
                            value={remark}
                            className={classnames("sco_inp_remark")}
                        />
                    </AvGroup>
                </CustomCardBody>
                <CustomCardFooter className="pt-1 pb-2">
                    <CustomSecondaryColorButton
                        outline
                        rounded
                        variant="light"
                        className="mr-2 cmn_btn_FRcancel"
                        onClick={() => {
                            if (isEdited === true) {
                                setIsActionsClicked(!isActionsClicked);
                                setActions(() => {
                                    setIsAddProject(!isAddProject);
                                    setDataIsUpdated(true);
                                    setIsEdited(false);
                                });
                            } else {
                                setIsAddProject(!isAddProject);
                            }
                        }}
                    >
                        <span> {t("Cancel")}</span>
                    </CustomSecondaryColorButton>
                    <CustomMainColorButton
                        rounded
                        disabled={
                            !isClickedBouttonValider || isFetchingTemplate
                        }
                        variant="primary"
                        onClick={handleValidate}
                        className="cmn_btn_FRvalidate"
                    >
                        {t("Validate")}
                    </CustomMainColorButton>
                </CustomCardFooter>
            </AvForm>
            <ConfirmationEditMessage
                isModalOpened={isModalOpened}
                setIsModalOpened={setIsModalOpened}
                setIsAddClicked={setIsAddProject}
                setEditedData={setSelectedRow}
            />
        </React.Fragment>
    );
}
